import { AxiosRequestConfig, AxiosResponse } from 'axios';
import Api from './api';

const TTL = 5 * 60 * 1000;

interface Entry {
  at: number;
  promise: Promise<AxiosResponse>;
  response?: AxiosResponse;
}

const store = new Map<string, Entry>();

const keyFor = (url: string, config?: AxiosRequestConfig) =>
  `${url}?${JSON.stringify(config?.params ?? {})}`;

const fresh = (entry: Entry | undefined) => !!entry && Date.now() - entry.at < TTL;

export function cachedGet<T>(url: string, config?: AxiosRequestConfig) {
  const key = keyFor(url, config);
  const hit = store.get(key);
  if (hit && fresh(hit)) return hit.promise as Promise<AxiosResponse<T>>;

  const entry: Entry = { at: Date.now(), promise: Api.get<T>(url, config) };
  entry.promise = entry.promise
    .then((response) => {
      entry.response = response;
      return response;
    })
    .catch((error) => {
      if (store.get(key) === entry) store.delete(key);
      throw error;
    });
  store.set(key, entry);
  return entry.promise as Promise<AxiosResponse<T>>;
}

export function peekGet<T>(url: string, config?: AxiosRequestConfig) {
  const hit = store.get(keyFor(url, config));
  return fresh(hit) ? (hit?.response as AxiosResponse<T> | undefined) : undefined;
}

export function invalidateCache(match: (url: string) => boolean) {
  Array.from(store.keys()).forEach((key) => {
    if (match(key.slice(0, key.indexOf('?')))) store.delete(key);
  });
}
